import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, getJSON } from '@/lib/api'
import { portalSpendCategories, type PortalSpendResponse } from './portalSpend.types'
import { PortalTransactionsTable } from './PortalTransactionsTable'
import { SearchBox } from './MemberPortalPeople'
import { PortalPagination } from './PortalPagination'

const categoryLabels: Record<string, string> = {
  membership: 'Membership',
  events: 'Events',
  training: 'Training',
  sponsorship: 'Sponsorship',
  other: 'Other',
}

// The organization's invoices, from the same organization-audience Spend list as the dashboard.
export function MemberPortalTransactions({
  organizationId,
  viewer,
  title = 'Transactions',
  redirectWhenMissing = true,
}: {
  organizationId: number
  viewer: string
  title?: string
  redirectWhenMissing?: boolean
}) {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [cursor, setCursor] = useState<string | null>(null)
  const path = transactionsPath(organizationId, search, category, cursor)
  const query = useQuery({
    queryKey: ['member-portal-dashboard', viewer, organizationId, 'transactions', path],
    queryFn: () => getJSON<PortalSpendResponse>(path),
    retry: false,
  })
  const data = query.isError ? undefined : query.data?.spend
  const missing = query.error instanceof ApiError && query.error.status === 404
  if (missing && redirectWhenMissing) return <Navigate to="/portal" replace />
  if (missing) return null
  const clear = () => {
    setSearch('')
    setCategory('all')
    setCursor(null)
  }
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <h2 className="text-body font-medium">{title}</h2>
          {data && (
            <span className="text-caption text-muted-foreground">· {data.total} invoices</span>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <SearchBox
            label="Search invoices"
            placeholder="Invoice number or person"
            value={search}
            onChange={(value) => {
              setSearch(value)
              setCursor(null)
            }}
          />
          <CategorySelect
            value={category}
            onChange={(value) => {
              setCategory(value)
              setCursor(null)
            }}
          />
        </div>
      </div>
      {query.isPending && <EmptyState variant="quiet" title="Loading invoices" />}
      {query.isError && (
        <Alert variant="destructive">
          Invoices unavailable.{' '}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {data && (
        <TransactionResults
          data={data}
          purchasedBy={category === 'training'}
          clear={clear}
          page={setCursor}
        />
      )}
    </Card>
  )
}

function transactionsPath(
  organizationId: number,
  search: string,
  category: string,
  cursor: string | null,
) {
  const request = new URLSearchParams()
  if (search) request.set('q', search)
  if (category !== 'all') request.set('categories', category)
  request.set('sort', 'issued:desc')
  if (cursor) request.set('cursor', cursor)
  return `/api/v1/member_portal/organizations/${organizationId}/dashboard/spend?${request}`
}

function CategorySelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label="Invoice category" className="h-9 w-48">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All categories</SelectItem>
        {portalSpendCategories.map((key) => (
          <SelectItem key={key} value={key}>
            {categoryLabels[key]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function TransactionResults({
  data,
  purchasedBy,
  clear,
  page,
}: {
  data: PortalSpendResponse['spend']
  purchasedBy: boolean
  clear: () => void
  page: (cursor: string) => void
}) {
  return (
    <>
      {data.rows.length ? (
        <PortalTransactionsTable rows={data.rows} purchasedBy={purchasedBy} />
      ) : (
        <EmptyState
          variant="quiet"
          title={data.available_total ? 'No invoices match' : 'No invoices yet'}
          action={
            data.available_total ? (
              <Button variant="outline" onClick={clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      )}
      <PortalPagination pagination={data.pagination} onPage={page} />
    </>
  )
}
